import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FolderKanban, TrendingUp, TrendingDown, Clock, DollarSign, Target, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";

export default function InternalProjects() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [showValueForm, setShowValueForm] = useState(false);
  const [estimatedValue, setEstimatedValue] = useState("");
  const [realizedValue, setRealizedValue] = useState("");
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ["internalProjects"],
    queryFn: () => base44.entities.Project.filter({ related_to_type: "internal" }, "-created_date"),
  });

  const { data: allTimeEntries = [] } = useQuery({
    queryKey: ["timeEntries"],
    queryFn: () => base44.entities.TimeEntry.list(),
  });

  const updateProjectMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Project.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["internalProjects"] });
      setShowValueForm(false);
      setSelectedProject(null);
    },
  });

  const getProjectMetrics = (project) => {
    const projectTimeEntries = allTimeEntries.filter(
      (entry) => entry.project_id === project.id
    );

    const totalHours = projectTimeEntries.reduce(
      (sum, entry) => sum + (entry.duration_minutes || 0) / 60,
      0
    );

    const totalCost = projectTimeEntries.reduce((sum, entry) => {
      const hourlyRate = project.hourly_rate || 2500;
      return sum + ((entry.duration_minutes || 0) / 60) * hourlyRate;
    }, 0);

    const estimatedValue = project.estimated_value || 0;
    const realizedValue = project.realized_value || 0;
    
    const roi = realizedValue > 0 ? ((realizedValue - totalCost) / totalCost) * 100 : 0;
    const potentialROI = estimatedValue > 0 ? ((estimatedValue - totalCost) / totalCost) * 100 : 0;

    return {
      totalHours,
      totalCost,
      estimatedValue,
      realizedValue,
      roi,
      potentialROI,
    };
  };

  const handleUpdateValue = () => {
    if (!selectedProject) return;
    
    updateProjectMutation.mutate({
      id: selectedProject.id,
      data: {
        ...selectedProject,
        estimated_value: parseFloat(estimatedValue) || 0,
        realized_value: parseFloat(realizedValue) || 0,
      },
    });
  };

  const openValueForm = (project) => {
    setSelectedProject(project);
    setEstimatedValue(project.estimated_value || "");
    setRealizedValue(project.realized_value || "");
    setShowValueForm(true);
  };

  // Calculate totals
  const totalMetrics = projects.reduce(
    (acc, project) => {
      const metrics = getProjectMetrics(project);
      return {
        totalCost: acc.totalCost + metrics.totalCost,
        totalRealizedValue: acc.totalRealizedValue + metrics.realizedValue,
        totalHours: acc.totalHours + metrics.totalHours,
      };
    },
    { totalCost: 0, totalRealizedValue: 0, totalHours: 0 }
  );

  const overallROI = totalMetrics.totalRealizedValue > 0 
    ? ((totalMetrics.totalRealizedValue - totalMetrics.totalCost) / totalMetrics.totalCost) * 100 
    : 0;

  const statusColors = {
    planning: "bg-slate-100 text-slate-700",
    active: "bg-blue-100 text-blue-700",
    on_hold: "bg-amber-100 text-amber-700",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-rose-100 text-rose-700",
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Interne Prosjekter</h1>
          <p className="text-slate-500 mt-1">Oversikt over kostnader og ROI på interne prosjekter</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 bg-white border-0 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Total Kostnad</span>
              <DollarSign className="w-4 h-4 text-rose-500" />
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {totalMetrics.totalCost.toLocaleString()} kr
            </p>
          </Card>

          <Card className="p-6 bg-white border-0 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Realisert Verdi</span>
              <Target className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {totalMetrics.totalRealizedValue.toLocaleString()} kr
            </p>
          </Card>

          <Card className="p-6 bg-white border-0 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Total Timer</span>
              <Clock className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {totalMetrics.totalHours.toFixed(1)} t
            </p>
          </Card>

          <Card className="p-6 bg-white border-0 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Samlet ROI</span>
              {overallROI >= 0 ? (
                <TrendingUp className="w-4 h-4 text-emerald-500" />
              ) : (
                <TrendingDown className="w-4 h-4 text-rose-500" />
              )}
            </div>
            <p className={cn(
              "text-2xl font-bold",
              overallROI >= 0 ? "text-emerald-600" : "text-rose-600"
            )}>
              {overallROI.toFixed(1)}%
            </p>
          </Card>
        </div>

        {/* Projects List */}
        {projectsLoading ? (
          <div className="grid grid-cols-1 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <Card className="p-12 text-center bg-white border-0 shadow-sm">
            <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Ingen interne prosjekter</h3>
            <p className="text-slate-500">
              Gå til Prosjekter for å opprette et nytt internt prosjekt
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {projects.map((project) => {
              const metrics = getProjectMetrics(project);
              return (
                <Card
                  key={project.id}
                  className="p-6 bg-white border-0 shadow-sm hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(createPageUrl("ProjectDetail") + "?id=" + project.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-slate-900">
                          {project.name}
                        </h3>
                        <Badge className={cn("border-0", statusColors[project.status])}>
                          {project.status}
                        </Badge>
                      </div>
                      {project.description && (
                        <p className="text-slate-600 mb-3">{project.description}</p>
                      )}
                      <p className="text-sm text-slate-500">
                        Eier: {project.owner_name || project.owner}
                      </p>
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        openValueForm(project);
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Oppdater Verdi
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-slate-100">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Timer Brukt</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {metrics.totalHours.toFixed(1)} t
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Kostnad</p>
                      <p className="text-lg font-semibold text-rose-600">
                        {metrics.totalCost.toLocaleString()} kr
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Estimert Verdi</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {metrics.estimatedValue > 0
                          ? `${metrics.estimatedValue.toLocaleString()} kr`
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Realisert Verdi</p>
                      <p className="text-lg font-semibold text-emerald-600">
                        {metrics.realizedValue > 0
                          ? `${metrics.realizedValue.toLocaleString()} kr`
                          : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">ROI</p>
                      <p
                        className={cn(
                          "text-lg font-semibold flex items-center gap-1",
                          metrics.realizedValue > 0
                            ? metrics.roi >= 0
                              ? "text-emerald-600"
                              : "text-rose-600"
                            : "text-slate-400"
                        )}
                      >
                        {metrics.realizedValue > 0 ? (
                          <>
                            {metrics.roi >= 0 ? (
                              <TrendingUp className="w-4 h-4" />
                            ) : (
                              <TrendingDown className="w-4 h-4" />
                            )}
                            {metrics.roi.toFixed(1)}%
                          </>
                        ) : (
                          "—"
                        )}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Value Form Modal */}
      {showValueForm && selectedProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-white">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-slate-900">
                  Oppdater Prosjektverdi
                </h2>
                <button
                  onClick={() => setShowValueForm(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              <p className="text-sm text-slate-600 mb-6">
                Prosjekt: <span className="font-medium">{selectedProject.name}</span>
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="estimatedValue">Estimert Verdi (NOK)</Label>
                  <Input
                    id="estimatedValue"
                    type="number"
                    min="0"
                    step="1000"
                    value={estimatedValue}
                    onChange={(e) => setEstimatedValue(e.target.value)}
                    placeholder="f.eks. 500000"
                    className="h-11"
                  />
                  <p className="text-xs text-slate-500">
                    Forventet verdi prosjektet vil generere
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="realizedValue">Realisert Verdi (NOK)</Label>
                  <Input
                    id="realizedValue"
                    type="number"
                    min="0"
                    step="1000"
                    value={realizedValue}
                    onChange={(e) => setRealizedValue(e.target.value)}
                    placeholder="f.eks. 450000"
                    className="h-11"
                  />
                  <p className="text-xs text-slate-500">
                    Faktisk verdi prosjektet har generert
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setShowValueForm(false)}
                  className="flex-1"
                >
                  Avbryt
                </Button>
                <Button
                  onClick={handleUpdateValue}
                  disabled={updateProjectMutation.isPending}
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                >
                  {updateProjectMutation.isPending ? "Lagrer..." : "Lagre"}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}